import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Play, ArrowRight, Flame } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Rail } from '../components/Rail';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Spinner } from '../components/ui/Spinner';
import { fmtDuration } from '../lib/utils';

export default function Discover() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [headline, setHeadline] = useState(null);

  useEffect(() => {
    api
      .get('/dashboard')
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));
    api
      .get('/settings/app-headline')
      .then(({ data }) => {
        const h = data.headline || data;
        if (h && h.enabled && h.title) setHeadline(h);
      })
      .catch(() => {});
  }, []);

  if (loading)
    return (
      <div className="grid h-[60vh] place-items-center">
        <Spinner className="h-8 w-8" />
      </div>
    );

  const hero = data?.today_lesson || data?.hero;

  return (
    <div className="space-y-10">
      <div>
        <p className="text-sm text-zinc-500">Welcome back</p>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-white">
          {user?.name?.split(' ')[0] || 'Trader'}
        </h1>
      </div>

      {headline && (
        <a
          href={headline.cta_url || '#'}
          target={headline.cta_url ? '_blank' : undefined}
          rel="noreferrer"
          onClick={(e) => !headline.cta_url && e.preventDefault()}
          data-testid="app-headline-banner"
          className="block overflow-hidden rounded-2xl border p-5 transition-transform hover:-translate-y-0.5"
          style={{ borderColor: `${headline.accent || '#10B981'}44`, background: `${headline.accent || '#10B981'}12` }}
        >
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="font-heading font-semibold text-white">{headline.title}</div>
              {headline.subtitle && <div className="text-sm text-zinc-400">{headline.subtitle}</div>}
            </div>
            {headline.cta_label && (
              <span className="shrink-0 rounded-full px-4 py-2 text-sm font-medium text-black" style={{ background: headline.accent || '#10B981' }}>
                {headline.cta_label}
              </span>
            )}
          </div>
        </a>
      )}

      {hero && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative overflow-hidden rounded-3xl border border-white/10"
        >
          <div className="grid md:grid-cols-2">
            <div className="relative aspect-video md:aspect-auto">
              {hero.thumbnail_url && (
                <img src={hero.thumbnail_url} alt={hero.title} className="h-full w-full object-cover" />
              )}
              <div className="absolute inset-0 bg-gradient-to-r from-ink-card/40 to-ink-card md:bg-gradient-to-r" />
            </div>
            <div className="flex flex-col justify-center gap-4 bg-ink-card p-8 lg:p-10">
              <Badge tone="emerald" className="w-fit">
                <Flame size={12} /> Pick up where you left off
              </Badge>
              <h2 className="font-heading text-2xl font-bold leading-tight text-white lg:text-3xl">{hero.title}</h2>
              <p className="text-sm text-zinc-400 line-clamp-3">{hero.description}</p>
              <div className="flex items-center gap-3 text-sm text-zinc-500">
                <span className="tnum">{fmtDuration(hero.duration_seconds)}</span>
                <span>•</span>
                <span>{hero.difficulty}</span>
              </div>
              <Button
                size="lg"
                variant="emerald"
                className="w-fit"
                onClick={() => navigate(`/app/lessons/${hero.lesson_id}`)}
                data-testid="hero-play-button"
              >
                <Play size={18} /> {hero.progress_seconds > 0 ? 'Resume lesson' : 'Start lesson'}
              </Button>
            </div>
          </div>
        </motion.div>
      )}

      <Rail
        title="Continue watching"
        lessons={data?.continue_watching}
      />
      <Rail
        title="Recommended for you"
        subtitle="Fresh lessons you haven't started"
        lessons={data?.recommended}
        action={
          <Button size="sm" variant="ghost" onClick={() => navigate('/app/lessons')} data-testid="see-all-lessons">
            All lessons <ArrowRight size={14} />
          </Button>
        }
      />
      <Rail title="Recently completed" lessons={data?.recently_completed} />

      {!hero && !data?.recommended?.length && (
        <div className="grain relative rounded-3xl border border-white/5 bg-ink-card p-16 text-center">
          <p className="font-heading text-lg text-white">No lessons yet</p>
          <p className="mt-2 text-sm text-zinc-500">Content will appear here once it's published.</p>
        </div>
      )}
    </div>
  );
}
